/**
 *
 * Service
 *
 */

import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { createStructuredSelector } from 'reselect';
import { isBot } from 'utils/stringUtils';
import { shouldShowContentfulTools } from 'containers/Auth/utils';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import useMixpanelTracking from 'components/useMixpanelTracking';
import PageResourceSkeleton from 'components/Skeletons/PageResourceSkeleton';
import { trackFavoriteAction } from 'containers/Favorites/utils';
import PageMeta from 'components/PageMeta';
import ItemPageResourcesRow from 'components/Resources/ItemPageResourcesRow';
import { makeSelectAuth, makeSelectProfile } from 'containers/Auth/selectors';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import withCustomBreakpoint from 'components/CustomBreakpoint';
import EmptyState from 'components/EmptyState';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import Divider from 'components/Divider';
import {
  makeSelectSavedResources,
  makeSelectRecommendedResources,
  makeSelectRelatedNews,
  makeSelectHelpfulRatings,
} from 'containers/Resources/selectors';
import Disclaimer from 'components/Disclaimer';
import {
  getSavedResources,
  saveResource,
  getRecommendedResources,
  getHelpfulRatings,
} from 'containers/Resources/actions';
import {
  isResourceSaved,
  getResourceAudience,
} from 'containers/Resources/utils';
import ResourcePageLayout from 'components/ResourcePageLayout';
import ItemPageDescriptionContainer from 'components/ItemPageDescriptionContainer';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import PaddedGrid from 'components/PaddedGrid';
import useSiteCopySelector from 'components/useSiteCopySelector';
import VisibilitySensor from 'react-visibility-sensor';
import ResourcePageHelpful from 'components/ResourcePageHelpful';
import { useFloatingHelpful } from 'components/ResourcePageHelpful/hooks/useFloatingHelpful';
import ServiceLinks from './ServiceLinks';
import ServiceInformation from './ServiceInformation';
import LocationsByService from './LocationsByService';
import OrganizationInformation from './OrganizationInformation';
import ServicesByOrganization from './ServicesByOrganization';
import RightArrowIcon from './images/chevron-right.svg';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: 18,
  },
  container: {
    // background: '#F8F9F9',
  },
  recommendedItemsWrapper: {
    marginTop: 20,
  },
  hiddenSection: {
    visibility: 'hidden',
  },
  descriptionContainer: {
    marginTop: '3.75rem',
  },
  btnLink: {
    ...theme.typography.button,
    width: 142,
    height: 35,
    background: '#fff',
    borderRadius: 2,
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #F8F9F9',
    color: theme.palette.primary.main,
    '& > img': {
      marginRight: 10,
    },
    marginBottom: '1rem',
    [theme.breakpoints.down('sm')]: {
      marginTop: '1rem',
    },
  },
}));

const stateSelector = createStructuredSelector({
  auth: makeSelectAuth(),
  clientDetails: makeSelectClientDetails(),
  profile: makeSelectProfile(),
  recommendedResources: makeSelectRecommendedResources(),
  savedResources: makeSelectSavedResources(),
  relatedNews: makeSelectRelatedNews(),
  ratings: makeSelectHelpfulRatings(),
});

const Service = ({
  queryInfo,
  match: {
    params: { slug },
  },
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const bot = isBot();
  const {
    auth,
    clientDetails,
    profile,
    recommendedResources,
    savedResources,
    relatedNews,
    ratings,
  } = useSelector(stateSelector);
  const location = useLocation();

  const { status, data } = queryInfo;
  const processing = status === 'loading';
  const item = _get(data, 'items.0', null);
  const showRecommendationsRow = !_get(
    clientDetails,
    'metadata.hideResourcePageRecommendations',
    false,
  );

  const [recommendBotSiteCopy, resourceItemPageSiteCopy] = useSiteCopySelector([
    'recommendbot-a',
    'resource-item-page',
  ]);

  useEffect(() => {
    if (profile.isLoaded) {
      dispatch(
        getHelpfulRatings({
          resourceID: _get(item, 'sys.id'),
          first: true,
          type: 'services',
        }),
      );
    }
  }, [item, profile]);

  const {
    showFloatingHelpful,
    toggleShowFloatingHelpful,
    toggleFloatingHelpful,
  } = useFloatingHelpful();

  useMixpanelTracking({
    eventTitle: 'Resource Page - Viewed',
    type: 'service',
    item,
  });

  useEffect(() => {
    if (auth.isLoaded && !auth.isEmpty && !bot) {
      dispatch(getSavedResources());
    }
  }, [auth]);

  useEffect(() => {
    if (status === 'success' && item !== null && !bot)
      dispatch(
        getRecommendedResources({
          type: 'Services',
          slug: _get(item, 'fields.slug'),
          topics: (_get(item, 'fields.topics') || []).map(el =>
            _get(el, 'fields.slug'),
          ),
          audience: getResourceAudience(data),
        }),
      );
  }, [item, status]);

  useEffect(() => {
    if (!processing && !window.prerenderReady) {
      window.prerenderReady = true;
    }
  }, [processing]);

  if (processing) return <PageResourceSkeleton />;
  if (_isEmpty(data.items) && !processing) return <EmptyState />;

  const handleSave = () => {
    const saved = isResourceSaved(savedResources, item);

    const parsedDescription = documentToHtmlString(
      _get(item, 'fields.description'),
    )
      .replace(/<\/?[^>]+(>|$)/g, '')
      .slice(0, 2000);

    let nodeID;
    if (saved)
      [{ nodeID }] = savedResources.filter(el => el.resourceID === item.sys.id);

    const saveData = {
      nodeID,
      resource: {
        type: 'Services',
        resourceID: item.sys.id,
        slug: _get(item, 'fields.slug'),
        name: _get(item, 'fields.name'),
        parsedDescription,
        imageUrl: _get(item, 'fields.imageUrl') || null,
      },
      save: !saved,
    };
    trackFavoriteAction(
      'Resource Page',
      {
        type: 'Services',
        resourceID: item.sys.id,
        slug: _get(item, 'fields.slug'),
        name: _get(item, 'fields.name'),
      },
      auth,
      saved,
    );
    dispatch(saveResource(saveData));
  };

  const {
    name = '',
    internalStatusIgnore = [],
    description = {},
    topics,
    type,
    locations,
  } = item.fields;
  const showContentfulTools = shouldShowContentfulTools(profile);
  const saved = isResourceSaved(savedResources, item);

  const stopRecommendBot = internalStatusIgnore.includes('Stop RecommendBot');

  const defaultParagraph = _get(recommendBotSiteCopy, 'body.json');
  if (
    !stopRecommendBot &&
    defaultParagraph &&
    !_get(description, 'content', '').includes(defaultParagraph)
  ) {
    description.content = description.content.concat(defaultParagraph);
  }
  const recommendBotConfig = _get(recommendBotSiteCopy, 'pageCopy');

  const acceptedTopics = (_get(item, 'fields.topics') || []).filter(
    ({ fields }) => !_isEmpty(fields) && fields.reviewStatus === 'Accepted',
  );

  const leftPanelData = {
    acceptedTopics,
    resourceData: data,
    actionProps: {
      isAdmin: showContentfulTools,
      saved,
      save: handleSave,
      toggleFloatingHelpful,
      ratings,
      type: 'service',
    },
  };

  const params = new URLSearchParams({
    filters: _get(location, 'state.filters') || [],
  });

  const content = () => (
    <>
      <Link
        to={{
          pathname: '/user-resources',
          search: params.toString(),
        }}
        className={classes.btnLink}
      >
        <img src={RightArrowIcon} alt="" />
        {_get(resourceItemPageSiteCopy, 'pageCopy.serviceAllServicesLink')}
      </Link>
      <ServiceInformation data={data} leftPanelData={leftPanelData} />
      <ItemPageDescriptionContainer className={classes.descriptionContainer}>
        <RichTextReactRenderer
          data={description}
          isDescription
          type="name"
          name={name}
          contentType="service"
          subtype={type}
          extraData={{
            stopRecommendBot,
            recommendBotConfig,
            resourceType: stopRecommendBot
              ? []
              : recommendedResources.filter(el => el.type !== 'Assessments'),
            assessments: stopRecommendBot
              ? []
              : recommendedResources.filter(el => el.type === 'Assessments'),
            topics:
              stopRecommendBot || !topics
                ? []
                : topics.filter(
                    el =>
                      !el.approach &&
                      _get(el, 'fields.reviewStatus') !== 'Internal',
                  ),
            approaches:
              stopRecommendBot || !topics
                ? []
                : topics.filter(el => el.approach),
            news: stopRecommendBot ? [] : relatedNews,
          }}
        />
      </ItemPageDescriptionContainer>
      <PaddedGrid item xs={12}>
        <Divider top={30} bottom={30} />
      </PaddedGrid>
      {!_isEmpty(data) && <ServiceLinks data={data.items} section="page" />}
      {!_isEmpty(locations) && <LocationsByService data={locations} />}
      <ServicesByOrganization
        organization={_get(data, 'items.0.fields.organization.0')}
        currentServiceId={_get(item, 'sys.id')}
      />
      {showRecommendationsRow && (
        <ItemPageResourcesRow
          header={_get(
            resourceItemPageSiteCopy,
            'pageCopy.recommendedResourcesHeader',
          )}
          data={recommendedResources}
          pageData={data.items}
          col={4}
        />
      )}
      <VisibilitySensor onChange={toggleShowFloatingHelpful}>
        <div className={classes.hiddenSection}>This is hidden</div>
      </VisibilitySensor>
      <OrganizationInformation
        data={data}
        recommendedResources={recommendedResources}
        relatedNews={relatedNews}
      />
    </>
  );
  return (
    <div className={classes.container}>
      <PageMeta
        data={data}
        type="service"
        canonicalUrl={`${window.location.origin}/services/${slug}`}
      />
      <ResourcePageLayout rightPanel={content} leftPanelData={leftPanelData} />
      <ResourcePageHelpful
        show={showFloatingHelpful}
        toggle={toggleFloatingHelpful}
        type="service"
        resourceID={_get(item, 'sys.id')}
        ratings={ratings}
        item={item}
      />
      <Disclaimer type="service" vendor={[name]} />
    </div>
  );
};

export default withCustomBreakpoint(Service);
